import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { useBookingModal } from "../context/BookingModalContext";
import BookingForm from "./BookingForm";

const BookingFormModal = () => {
  const { isOpen, closeBookingModal } = useBookingModal();

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isOpen) closeBookingModal();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, closeBookingModal]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[200] flex items-start justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={closeBookingModal}
            data-testid="booking-modal-backdrop"
          />

          {/* Modal content */}
          <motion.div
            className="relative z-10 w-full max-w-4xl mx-4 my-6 max-h-[calc(100vh-3rem)] overflow-y-auto rounded-2xl bg-[#fafafa] shadow-2xl"
            initial={{ opacity: 0, y: 40, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.97 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            role="dialog"
            aria-modal="true"
            aria-label="Get your quote"
            data-testid="booking-modal"
          >
            {/* Close button */}
            <button
              onClick={closeBookingModal}
              data-testid="booking-modal-close"
              aria-label="Close booking form"
              className="sticky top-4 float-right mr-4 mt-4 z-20 w-10 h-10 rounded-full bg-white shadow-lg flex items-center justify-center text-[#444444] hover:text-[#1a1a1a] hover:bg-gray-100 transition-all focus:outline-none focus:ring-2 focus:ring-[#a5dae0]"
            >
              <X size={20} />
            </button>

            <BookingForm isModal onClose={closeBookingModal} />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default BookingFormModal;
